import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocationData } from 'hooks/useLocationData';
import { ApiListingsRepository } from 'services/api/listings/ApiListingsRepository';
import type { CompanyFormData } from 'pages/editor/useCreateCompany';
import { useProspect } from 'components/editor/SidePanel/DocumentSettings/useProspect';
import { PyEntityCreationForm } from '../PyEntityCreationForm';
import { validateField, normalizeWebsiteUrl, isFormValid, mapToRequest } from './companyForm';

const apiListingsRepository = new ApiListingsRepository();
const initialFormValues: Record<string, string> = {
  name: '',
  city: '',
  address1: '',
  address2: '',
  countryId: '',
  stateId: '',
  zipCode: '',
  websiteUrl: '',
};

const CreateCompanyForm = ({
  onValidityChange,
  onFormDataChange,
}: {
  onValidityChange?: (isValid: boolean) => void;
  onFormDataChange?: (data: CompanyFormData) => void;
}) => {
  const { locationData, getStatesFromCountry } = useLocationData({
    listingsRepository: apiListingsRepository,
  });
  const { t } = useTranslation();
  const { options: prospectOptions } = useProspect();

  const {
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors, touchedFields },
  } = useForm<Record<string, string>>({
    defaultValues: initialFormValues,
  });

  const formValues = watch();

  const applyValidation = (field: string, value: string) => {
    const { error } = validateField(field, value, t, prospectOptions || []);
    error ? setError(field, { type: 'validate', message: error }) : clearErrors(field);
    return error;
  };

  const notifyParent = (validatedField?: string, fieldError?: string) => {
    const currentValues = getValues();
    const hasErrors = validatedField
      ? !!fieldError || Object.keys(errors).filter((k) => k !== validatedField).length > 0
      : Object.keys(errors).length > 0;
    onValidityChange?.(isFormValid(currentValues, hasErrors));
    onFormDataChange?.(mapToRequest(currentValues));
  };

  const handleBlur = (field: string, value: string) => {
    const nextValue = field === 'websiteUrl' ? normalizeWebsiteUrl(value) : value;
    setValue(field, nextValue, { shouldTouch: true });
    const error = applyValidation(field, nextValue);
    notifyParent(field, error);
  };

  const handleInputChange = (field: string, value: string) => {
    setValue(field, value);
    if (touchedFields[field]) {
      const error = applyValidation(field, value);
      notifyParent(field, error);
    } else {
      notifyParent();
    }
  };

  const handleCountryChange = (countryId: string) => {
    setValue('countryId', countryId);
    setValue('stateId', '');
    notifyParent();
  };

  const formFields = [
    {
      id: 'name',
      label: 'Company Name',
      type: 'text' as const,
      placeholder: 'Enter company name',
      error: !!errors['name'],
      helperText: errors['name']?.message || '',
      required: true,
    },
    {
      id: 'city',
      label: 'City',
      type: 'text' as const,
      placeholder: 'Enter city',
      error: !!errors['city'],
      helperText: errors['city']?.message || '',
    },
    {
      id: 'address1',
      label: 'Address Line 1',
      type: 'text' as const,
      placeholder: 'Enter address line 1',
      error: !!errors['address1'],
      helperText: errors['address1']?.message || '',
    },
    {
      id: 'address2',
      label: 'Address Line 2',
      type: 'text' as const,
      placeholder: 'Enter address line 2',
      error: !!errors['address2'],
      helperText: errors['address2']?.message || '',
    },
    {
      id: 'countryId',
      label: 'Country',
      type: 'select' as const,
      placeholder: 'Select country',
      options:
        locationData?.countries.map((country) => ({
          label: country.name,
          value: country.id.toString(),
        })) || [],
    },
    {
      id: 'stateId',
      label: 'State/Province',
      type: 'select' as const,
      placeholder: 'Select state/province',
      options: getStatesFromCountry(
        formValues.countryId ? Number(formValues.countryId) : 0,
        locationData?.states || [],
        locationData?.countries || []
      ).map((state) => ({
        label: state.name,
        value: state.id.toString(),
      })),
      isDisabled: !formValues.countryId,
    },
    {
      id: 'zipCode',
      label: 'Zip/Postal Code',
      type: 'text' as const,
      placeholder: 'Enter zip/postal code',
      error: !!errors['zipCode'],
      helperText: errors['zipCode']?.message || '',
    },
    {
      id: 'websiteUrl',
      label: 'Website URL',
      type: 'text' as const,
      placeholder: 'Enter website URL',
      error: !!errors['websiteUrl'],
      helperText: errors['websiteUrl']?.message || '',
    },
  ];

  return (
    <PyEntityCreationForm
      formFields={formFields}
      initialValues={formValues}
      onFormDataChange={(newValues) => {
        Object.entries(newValues).forEach(([field, value]) => {
          if (typeof value === 'string') {
            handleInputChange(field, value);
          }
        });
      }}
      onValidityChange={onValidityChange}
      textInputValidation={{
        name: { onBlur: (value) => handleBlur('name', value) },
        city: { onBlur: (value) => handleBlur('city', value) },
        address1: { onBlur: (value) => handleBlur('address1', value) },
        address2: { onBlur: (value) => handleBlur('address2', value) },
        zipCode: { onBlur: (value) => handleBlur('zipCode', value) },
        websiteUrl: { onBlur: (value) => handleBlur('websiteUrl', value) },
      }}
      selectInputHandlers={{
        countryId: handleCountryChange,
      }}
    />
  );
};

export default CreateCompanyForm;
